import { useContext, useMemo } from "react";
import {
  UNSAFE_DataRouterStateContext as DataRouterStateContext,
  UNSAFE_FrameworkContext as FrameworkContext,
  Scripts,
  type ScriptsProps,
} from "react-router";

/**
 * COM-523: React Router's <Scripts>, but modulepreloading only the entry and
 * the matched route modules at normal priority — the rest of their static
 * import graph is preloaded at fetchpriority="low".
 *
 * Stock <Scripts> emits one `<link rel="modulepreload">` per file in the
 * matched routes' import graph (44 on the homepage). React 19 hoists them
 * into <head> ahead of the non-hoisted root stylesheet, so ~350 KB of JS
 * starts downloading at the same priority tier as the render-blocking CSS,
 * the fonts and the LCP image.
 *
 * Here the manifest handed to <Scripts> has its `imports` lists emptied, so
 * only the entry and route modules are preloaded at normal priority. The
 * inline hydration script still imports every route module, so nothing is
 * skipped.
 *
 * The emptied `imports` also reach the partial manifest <Scripts> inlines for
 * the client router. Only the SSR-matched routes are in it, and their modules
 * are already loaded by then; routes discovered later come from /__manifest
 * with their full `imports`, so navigation preloading is unchanged.
 */
export function LeanScripts(props: ScriptsProps) {
  const context = useContext(FrameworkContext);
  const lean = useMemo(() => {
    if (!context) return context;
    const { manifest } = context;
    const routes: typeof manifest.routes = {};
    for (const [id, route] of Object.entries(manifest.routes)) {
      routes[id] = route && { ...route, imports: undefined };
    }
    return {
      ...context,
      manifest: {
        ...manifest,
        entry: { ...manifest.entry, imports: [] },
        routes,
      },
    };
  }, [context]);
  return (
    <>
      <FrameworkContext.Provider value={lean}>
        <Scripts {...props} />
      </FrameworkContext.Provider>
      <LowPriorityModulePreloads {...props} />
    </>
  );
}

/**
 * Preloads the static imports LeanScripts leaves out, at fetchpriority="low".
 *
 * Without them the import graph is only requested once the hydration script
 * at the end of <body> is parsed (modulepreload does not fetch a module's own
 * imports), and the header became interactive ~1.3 s later on the homepage
 * (4x CPU, slow 4G) despite the earlier first paint. At low priority they
 * start straight away but yield to the CSS, the fonts and the LCP image.
 *
 * Rendered outside LeanScripts' provider, so it reads the full manifest.
 * React 19 hoists these links into <head>, and hoisted elements are not
 * matched on hydrate, so it does not matter that the client renders none
 * (the manifest it hydrates from has the `imports` emptied above).
 */
function LowPriorityModulePreloads({ nonce, crossOrigin }: ScriptsProps) {
  const context = useContext(FrameworkContext);
  const state = useContext(DataRouterStateContext);
  const manifest = context?.manifest;
  if (!manifest) return null;
  const routes = (state?.matches ?? []).map(
    match => manifest.routes[match.route.id],
  );
  // Already preloaded at normal priority by the lean <Scripts>.
  const early = new Set([
    manifest.entry.module,
    ...routes.map(route => route?.module),
  ]);
  const hrefs = new Set([
    ...manifest.entry.imports,
    ...routes.flatMap(route => route?.imports ?? []),
  ]);
  return [...hrefs]
    .filter(href => !early.has(href))
    .map(href => (
      <link
        key={href}
        rel="modulepreload"
        href={href}
        fetchPriority="low"
        crossOrigin={crossOrigin}
        nonce={nonce}
      />
    ));
}
